import React, { useEffect, useState, useMemo, useRef } from 'react'
import { DialogContent, MenuItem, Checkbox, Typography, InputAdornment, CircularProgress, Button, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ArrowBackIosOutlined, ArrowForwardIosOutlined } from '@material-ui/icons'
import CalendarIconButton from '../../Common/Buttons/CalendarIconButton'
import StyledDialog from '../../Common/Dialogs/StyledDialog'
import StyledTextField from '../../Common/StyledTextField'
import StyledMultiSelect from '../../Common/StyledMultiSelect'
import StyledSelect from '../../Common/StyledSelect'
import StyledDatePicker from '../../Common/StyledDatePicker'
import StyledSwitchWithLabel from '../../Common/StyledSwitchWithLabel'
import StyledKeyboardTimePicker from '../../Common/StyledKeyboardTimePicker'
import StyledCurrencyTextField from '../../Common/StyledCurrencyTextField'
import StyledAlert from '../../Common/StyledAlert'
import StyledIconWithTooltip from '../../Common/StyledIconWithTooltip'
import { MainButton } from '../../Button/MainButton'
import { DangerButton } from '../../Button/DangerButton'
import DangerConfirmationDialog from '../../Common/Dialogs/DangerConfirmation'
import {
	NON_BREAKING_SPACE,
	PROMO_STATUSES,
	PROMO_TEMPLATE,
	PROMO_DAYS_OF_WEEK,
	PROMO_SERVICE_TARGET_OPTIONS,
	PROMO_GUIDELINES_LINK,
	PROMO_SERVICE_TARGET_UNIT_OPTIONS,
	PROMO_MEMBERSHIP_TARGET_OPTIONS,
	PROMO_MEMBERSHIP_TYPE_TARGET_OPTIONS,
	DIALOG_EDIT_MODES,
	getKeyForValueInObject,
	capitilizeMultipleWords,
	onlyNumbers,
	GROOMING,
	BAR_PARK,
	standardizeEndOfDateUTC,
	standardizeStartOfDateUTC,
	BOARDING,
	TRAINING,
	FEE_VALUE_UNITS,
	RENEWAL_LIMITS,
	PROMO_SERVICE_TARGETS,
	PROMO_LINK_TYPES,
	PROMO_APP_LINK_TARGETS,
	CLIENT_APP_PROMO_LIMIT
} from '../../../utils'
import usePromotions from '../../../hooks/usePromotions'
import useConstants from '../../../hooks/useConstants'
import moment from 'moment'
import { api } from '../../../services/api'
import { useDispatch } from 'react-redux'
import { showError, showSuccess } from '../../../actions/misc/misc.actions'

const useStyles = makeStyles((theme) => ({
	dialogContent: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		padding: '0 1.5rem',
		gap: '1rem'
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		'& > div': {
			flex: 1
		},
		gap: '1.5rem'
	},
	datePicker: {
		margin: 0,
		'& .MuiInputLabel-filled': {
			transform: 'translate(48px, 20px) scale(1)'
		},
		'& .MuiInputLabel-shrink': {
			transform: 'translate(48px, 10px) scale(0.75)'
		}
	},
	calendarHeaderButtons: {
		fill: theme.palette.primary.main
	},
	dialogActions: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		gap: '1rem',
		margin: '2rem 0 1rem 0',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		}
	},
	amountAdornment: {
		marginLeft: '10px',
		marginTop: '17px !important'
	},
	timePickersContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		flex: 1
	},
	timePickersContent: { display: 'flex', justifyContent: 'space-between', gap: '1rem' },
	switchContainer: { display: 'flex', alignItems: 'flex-end' },
	fileUpload: {
		display: 'none'
	}
}))

const managePercentEntry = (value) => {
	const regex = /^\d+$/
	if (regex.test(value) === false) {
		return 0
	}

	const valueAsInt = parseInt(value, 10)
	if (valueAsInt > 100) {
		return 100
	}
	return valueAsInt
}

/**
 *
 * @param {{
  open: Boolean, 
	onClose: Function, 
	onCancelled: Function,
	title: String,
	mode: "new" | "edit", 
	promo: {	
		name: String,
		code: String,
		value: Number,
		unit: 'percent' | 'cents',
		description: String,
		fine_print: String,
		start_date: Date,
		start_time: String | null,
		end_date: null,
		end_time: String | null,
		redemptions_per_household: Number | null,
		new_customers_only: Boolean,
		max_total_redemptions: Number | null,
		max_discount: Number | null,
		applicable_days_of_week: String[],
		applicable_facilities: String[],
		applicable_markets: String[],
		applicable_services: String[],
		applicable_addons: String[],
		applicable_memberships: String[],
		created_by: String,
		min_number_of_nights: Number | null
	}}} params
 * @returns
 */
const PromoDialog = ({
	open,
	onSaved,
	onCancelled,
	title = 'Promo',
	mode = DIALOG_EDIT_MODES.New,
	promo = {},
	markets = [],
	facilities = [],
	lockToFacilityId = null,
	allowMultipleFacilities = true
}) => {
	const classes = useStyles()

	const [editedPromo, setEditedPromo] = useState(promo)
	const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false)
	const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false)
	const [editedKeys, setEditedKeys] = useState(new Set())
	const [confirmEndPromoDialogOpen, setConfirmEndPromoDialogOpen] = useState(false)
	const [imageLoading, setImageLoading] = useState(false)
	const promoImageInputRef = useRef(null)
	const dispatch = useDispatch()

	const { getExistingPromosInStateWithSameCode, savePromo, updatePromo, promosBusy, promotions } = usePromotions()
	const { trainingCourseIdentifiers } = useConstants()

	useEffect(() => {
		const nextPromo = { ...promo }
		if (lockToFacilityId) {
			nextPromo.applicable_facilities = [lockToFacilityId]
		}
		setEditedPromo(nextPromo)
	}, [promo, lockToFacilityId])

	// console.log('ee....', editedPromo)

	const filteredTrainingCourseIdentifiers = useMemo(() => {
		const courses = trainingCourseIdentifiers?.filter((course) => {
			return course.name !== 'Play & Study'
		})
		return courses
	}, [trainingCourseIdentifiers])

	const codeContainsSpecialCharacters = useMemo(() => {
		const { code } = editedPromo
		if (code === '') {
			return false
		}

		const regex = /[!@#$%^&*(),.?"\':{}<>]/g
		return regex.test(code)
	}, [editedPromo])

	// Count active promos with show_in_client_app enabled (excluding current promo being edited)
	const activeClientAppPromosCount = useMemo(() => {
		if (!promotions || !Array.isArray(promotions)) {
			return 0
		}

		const nowUTC = moment().utc()
		const currentPromoId = editedPromo._id

		return promotions.filter((p) => {
			// Exclude the current promo being edited
			if (p._id === currentPromoId) {
				return false
			}

			// Must have show_in_client_app enabled
			if (!p.show_in_client_app) {
				return false
			}

			// Must not be deactivated
			if (p.deactivated) {
				return false
			}

			// Must be active (Running or Scheduled)
			const startDate = standardizeStartOfDateUTC(p.start_date)
			const endDate = standardizeEndOfDateUTC(p.end_date)

			// Check if promo is currently active (not past end date)
			if (nowUTC.isAfter(endDate)) {
				return false
			}

			// Promo is active if it's scheduled (before start) or running (between start and end)
			return nowUTC.isBefore(startDate) || nowUTC.isBetween(startDate, endDate, null, '[]')
		}).length
	}, [promotions, editedPromo._id])

	// Check if enabling show_in_client_app would exceed the limit. Only relevant when
	// turning it on — a promo that is already displayed must stay switchable off even
	// once the cap has been reached.
	const wouldExceedClientAppLimit = useMemo(() => {
		if (editedPromo.show_in_client_app) return false

		return activeClientAppPromosCount + 1 > CLIENT_APP_PROMO_LIMIT
	}, [
		editedPromo.show_in_client_app,
		activeClientAppPromosCount,
		editedPromo.start_date,
		editedPromo.end_date,
		editedPromo.deactivated
	])

	const handleResetDialog = () => {
		setEditedPromo({ ...PROMO_TEMPLATE })
		setEditedKeys(new Set())
		if (promoImageInputRef.current) {
			promoImageInputRef.current.value = ''
		}
	}

	const uploadPromoImage = async (e) => {
		try {
			if (editedPromo._id) {
				setImageLoading(true)
				if (e.target.files && e.target.files.length && e.target.files[0]) {
					const file = e.target.files[0]
					const data = new FormData()
					data.append('promo_image', file)

					const promoData = await api.put(`/promoImages/${editedPromo._id}`, data)
					setEditedPromo({
						...editedPromo,
						image_url: promoData.data.image_url
					})
					setEditedKeys((keys) => keys.add('image_url'))
				}
				dispatch(showSuccess('Image uploaded. Save promo to apply changes.'))
				setImageLoading(false)
			} else {
				// For new promos, store the file temporarily
				if (e.target.files && e.target.files.length && e.target.files[0]) {
					setEditedPromo({
						...editedPromo,
						temp_image_file: e.target.files[0]
					})
				}
			}
		} catch (e) {
			console.log('err...', e)
			dispatch(showError('Error uploading image.'))
			setImageLoading(false)
		}
	}

	const uploadImageForNewPromo = async () => {
		if (!editedPromo.temp_image_file) {
			return null
		}
		try {
			const data = new FormData()
			data.append('promo_image', editedPromo.temp_image_file)
			const promoData = await api.put(`/promoImages/newOne`, data)
			return promoData.data.image_url
		} catch (e) {
			console.log('err...', e)
			dispatch(showError('Error uploading image.'))
			return null
		}
	}

	const handleOnCancelled = () => {
		if (onCancelled) {
			handleResetDialog()
			onCancelled()
		}
	}

	const handleOnSave = async () => {
		if (lockToFacilityId) {
			editedPromo.applicable_facilities = [lockToFacilityId]
		}
		if (mode === DIALOG_EDIT_MODES.New) {
			// Upload image if it's a new promo with a temp file
			if (editedPromo.temp_image_file) {
				const imageUrl = await uploadImageForNewPromo()
				if (imageUrl) {
					editedPromo.image_url = imageUrl
				}
			}
			await savePromo(editedPromo)
		}
		if (mode === DIALOG_EDIT_MODES.Edit && editedKeys.size > 0) {
			const updates = {
				code: editedPromo.code.toUpperCase(),
				start_date: standardizeStartOfDateUTC(editedPromo.start_date).format('YYYY-MM-DD'),
				end_date: standardizeEndOfDateUTC(editedPromo.end_date).format('YYYY-MM-DD')
			}
			for (const key of editedKeys) {
				updates[key] = editedPromo[key]
			}

			const { _id } = editedPromo
			await updatePromo({ updates, promo_id: _id })
			setEditedKeys(new Set())
		}
		if (onSaved) {
			handleResetDialog()
			onSaved()
		}
	}

	const handleCloseConfirmEndPromoDialog = () => {
		setConfirmEndPromoDialogOpen(false)
	}

	const handleEndPromo = async () => {
		await updatePromo({ updates: { deactivated: true }, promo_id: editedPromo._id })
		handleCloseConfirmEndPromoDialog()
		if (onSaved) {
			handleResetDialog()
			onSaved()
		}
	}

	const handlePromoFieldUpdate = ({ field, targetValue }) => {
		setEditedPromo((prev) => {
			const revisedPromo = { ...prev, [field]: targetValue }

			if (
				field === 'value' &&
				revisedPromo.unit === getKeyForValueInObject(FEE_VALUE_UNITS, FEE_VALUE_UNITS.percent)
			) {
				revisedPromo.value = managePercentEntry(targetValue)
			}

			if (field === 'unit') {
				revisedPromo.value = null // if unit changes always reset value of promo
				// if unit is changes to cents, then reset max_discount back to null
				if (targetValue === getKeyForValueInObject(FEE_VALUE_UNITS, FEE_VALUE_UNITS.cents)) {
					revisedPromo.max_discount = null
				}
			}

			if (field === 'redemptions_per_household') {
				revisedPromo.redemptions_per_household = onlyNumbers(targetValue)
			}

			if (field === 'renewal_limit') {
				revisedPromo.renewal_limit = onlyNumbers(targetValue)
			}

			if (field === 'min_number_of_nights') {
				revisedPromo.min_number_of_nights = onlyNumbers(targetValue)
			}

			return revisedPromo
		})
		setEditedKeys((keys) => keys.add(field))
	}

	const startDateToMoment = useMemo(() => {
		return standardizeStartOfDateUTC(editedPromo.start_date)
	}, [editedPromo.start_date])

	const endDateToMoment = useMemo(() => {
		return standardizeEndOfDateUTC(editedPromo.end_date)
	}, [editedPromo.end_date])

	const startTimeToMoment = useMemo(() => {
		const { start_time } = editedPromo
		return moment().set({
			hour: start_time ? start_time.split(':')[0] : 0,
			minute: start_time ? start_time.split(':')[1] : 0
		})
	}, [editedPromo.start_time])

	const endTimeToMoment = useMemo(() => {
		const { end_time } = editedPromo
		return moment().set({
			hour: end_time ? end_time.split(':')[0] : 0,
			minute: end_time ? end_time.split(':')[1] : 0
		})
	}, [editedPromo.end_time])

	const promoIncludesUnitNotAllowedForServiceTarget = useMemo(() => {
		const { applicable_services, unit } = editedPromo
		const errors = []

		if (applicable_services.length === 0 || unit === '') {
			return []
		}

		applicable_services.forEach((service) => {
			const allowedUnits = PROMO_SERVICE_TARGET_UNIT_OPTIONS[service]
			if (!allowedUnits.includes(unit)) {
				errors.push(
					`${PROMO_SERVICE_TARGET_OPTIONS[service]} cannot have a ${FEE_VALUE_UNITS[unit]} discount.`
				)
			}
		})
		return errors
	}, [editedPromo])

	const promoWithSameCodeAlreadyRunningOrScheduled = useMemo(() => {
		const { code, _id } = editedPromo
		const samePromos = getExistingPromosInStateWithSameCode(code.toUpperCase())
		const found = samePromos.find((promo) => {
			if (promo._id === _id) {
				return false
			}
			if (promo.status !== PROMO_STATUSES.Running && promo.status !== PROMO_STATUSES.Scheduled) {
				return false
			}

			const existingPromoStartDate = standardizeStartOfDateUTC(promo.start_date)
			const existingPromoEndDate = standardizeEndOfDateUTC(promo.end_date)

			if (startDateToMoment.isBetween(existingPromoStartDate, existingPromoEndDate, null, '[]')) {
				return true
			}

			if (endDateToMoment.isBetween(existingPromoStartDate, existingPromoEndDate, null, '[]')) {
				return true
			}

			if (existingPromoStartDate.isBetween(startDateToMoment, endDateToMoment, null, '[]')) {
				return true
			}

			if (existingPromoEndDate.isBetween(startDateToMoment, endDateToMoment, null, '[]')) {
				return true
			}

			return false
		})

		return found
	}, [getExistingPromosInStateWithSameCode, editedPromo, startDateToMoment, endDateToMoment])

	const enableTimeEditing = useMemo(() => {
		return (
			editedPromo.applicable_services.length > 0 &&
			(editedPromo.applicable_services.includes(GROOMING) ||
				editedPromo.applicable_services.includes(BAR_PARK))
		)
	}, [editedPromo])

	/**
	 * Every reason the promo cannot be saved, phrased for the person filling out the form.
	 * Surfaced next to the Save button so a disabled button always explains itself —
	 * several of these fields live in collapsed sections and are easy to miss.
	 *
	 * Reasons already rendered as their own alert below (boarding days of week, start/end
	 * date order, promo code characters, service target units) are left out to avoid
	 * showing the same message twice.
	 */
	const saveBlockers = useMemo(() => {
		const blockers = []

		if (promoWithSameCodeAlreadyRunningOrScheduled) {
			blockers.push('Another running or scheduled promo already uses this code.')
		}

		if (!editedPromo.name) blockers.push('Promo Name is required.')

		if (!editedPromo.code) {
			blockers.push('Promo Code is required.')
		} else if (editedPromo.code.length <= 4) {
			blockers.push('Promo Code must be at least 5 characters.')
		}

		if (!editedPromo.unit) blockers.push('Discount Type is required.')

		const valueToInt = parseInt(editedPromo.value, 10)
		if (Number.isNaN(valueToInt) || valueToInt <= 0) {
			blockers.push('Promo Amount must be greater than 0.')
		}

		if (
			editedPromo.unit === getKeyForValueInObject(FEE_VALUE_UNITS, FEE_VALUE_UNITS.percent) &&
			editedPromo.max_discount !== null &&
			editedPromo.max_discount <= 0
		) {
			blockers.push('Discount Limit must be greater than 0 for percent promos.')
		}

		if (!editedPromo.fine_print) blockers.push('Promo Fine Print is required.')

		if (!editedPromo.start_date) blockers.push('Start Date is required.')
		if (!editedPromo.end_date) blockers.push('End Date is required.')

		if (editedPromo.start_time && !editedPromo.end_time) {
			blockers.push('End Time is required when a Start Time is set.')
		}

		if (!editedPromo.start_time && editedPromo.end_time) {
			blockers.push('Start Time is required when an End Time is set.')
		}

		if (endTimeToMoment.isBefore(startTimeToMoment)) {
			blockers.push('End Time is before Start Time.')
		}

		if (!editedPromo.applicable_services.length) blockers.push('At least one Service Target is required.')

		if (!(editedPromo.applicable_facilities || []).length) {
			blockers.push('At least one facility is required.')
		}

		if (!(editedPromo.redemptions_per_household > 0)) {
			blockers.push('Redemptions per household must be greater than 0.')
		}

		if (
			editedPromo.applicable_services.length &&
			editedPromo.applicable_services.every((service) =>
				[PROMO_SERVICE_TARGETS.BOARDING, PROMO_SERVICE_TARGETS.BOARDING_WITH_CREDITS].includes(service)
			) &&
			editedPromo.min_number_of_nights < 1
		) {
			blockers.push('Boarding-only promos require a minimum number of nights of at least 1.')
		}

		if (
			editedPromo.applicable_services.includes('memberships') &&
			editedPromo.membership_type_targets.length === 0
		) {
			blockers.push('At least one Membership Type Target is required for membership promos.')
		}

		// Client app display needs the extra content fields that render the promo card.
		if (editedPromo.show_in_client_app) {
			const hasImage =
				mode === DIALOG_EDIT_MODES.New
					? editedPromo.temp_image_file ||
					  (editedPromo.image_url && editedPromo.image_url.trim() !== '')
					: editedPromo.image_url && editedPromo.image_url.trim() !== ''

			if (!hasImage) blockers.push('Show in Client App requires an image.')

			if (!editedPromo.link_type || editedPromo.link_type === '') {
				blockers.push('Show in Client App requires a Link Type.')
			}

			if (
				editedPromo.link_type === PROMO_LINK_TYPES.Web &&
				(!editedPromo.web_link_url || editedPromo.web_link_url.trim() === '')
			) {
				blockers.push('Show in Client App requires a web link URL.')
			}

			if (
				editedPromo.link_type === PROMO_LINK_TYPES.App &&
				(!editedPromo.app_link_target || editedPromo.app_link_target === '')
			) {
				blockers.push('Show in Client App requires an app link target.')
			}

			if (!editedPromo.headline || editedPromo.headline.trim() === '') {
				blockers.push('Show in Client App requires a headline.')
			}

			if (!editedPromo.short_description || editedPromo.short_description.trim() === '') {
				blockers.push('Show in Client App requires a short description.')
			}
		}

		return blockers
	}, [promoWithSameCodeAlreadyRunningOrScheduled, editedPromo, startTimeToMoment, endTimeToMoment, mode])

	const canSave = useMemo(() => {
		if (saveBlockers.length > 0) return false

		if (
			editedPromo.applicable_services.includes(BOARDING) &&
			editedPromo.applicable_days_of_week.length > 0
		) {
			return false
		}

		if (startDateToMoment.isAfter(endDateToMoment)) return false

		if (editedPromo.code && codeContainsSpecialCharacters) return false

		if (promoIncludesUnitNotAllowedForServiceTarget.length > 0) return false

		return true
	}, [
		saveBlockers,
		editedPromo.applicable_services,
		editedPromo.applicable_days_of_week,
		editedPromo.code,
		startDateToMoment,
		endDateToMoment,
		codeContainsSpecialCharacters,
		promoIncludesUnitNotAllowedForServiceTarget
	])

	// Facility Related ------------------------------------------------------------
	useEffect(() => {
		if (!lockToFacilityId) return
		setEditedPromo((current) => ({
			...current,
			applicable_facilities: [lockToFacilityId]
		}))
		setEditedKeys((keys) => keys.add('applicable_facilities'))
	}, [lockToFacilityId])

	const handleFacilityFieldUpdate = (facilityIds) => {
		if (lockToFacilityId || !allowMultipleFacilities) return

		setEditedPromo({ ...editedPromo, applicable_facilities: facilityIds })
		setEditedKeys((keys) => keys.add('applicable_facilities'))
	}
	// End Facility Related --------------------------------------------------------

	return (
		<>
			<StyledDialog
				open={open}
				onClose={onCancelled}
				title={title}
				showActivityIndicator={promosBusy}
				titleActions={
					mode !== DIALOG_EDIT_MODES.New && mode !== DIALOG_EDIT_MODES.ViewOnly ? (
						<div style={{ marginRight: '1rem' }}>
							<DangerButton
								color="primary"
								label="End Promo"
								onClick={() => setConfirmEndPromoDialogOpen(true)}
							/>
						</div>
					) : null
				}>
				<DialogContent>
					<div className={classes.dialogContent}>
						<div className={classes.row}>
							<div>
								<StyledTextField
									header="Promo Name (Internal)"
									variant="filled"
									label="Promo Name"
									value={editedPromo.name}
									required={true}
									disabled={
										[
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
									}
									onChange={(e) =>
										handlePromoFieldUpdate({ field: 'name', targetValue: e.target.value })
									}
								/>
							</div>
							<div>
								<StyledTextField
									header="Promo Code"
									variant="filled"
									label="Promo Code"
									disabled={
										promo.status !== PROMO_STATUSES.Draft ||
										mode === DIALOG_EDIT_MODES.ViewOnly
									}
									value={editedPromo.code}
									inputProps={{ maxLength: 15 }}
									required={true}
									onChange={(e) =>
										handlePromoFieldUpdate({ field: 'code', targetValue: e.target.value })
									}
								/>
							</div>
						</div>
						<div className={classes.row}>
							<StyledTextField
								header="Promo Notes (Internal)"
								variant="filled"
								minRows={3}
								label="Note..."
								value={editedPromo.description}
								multiline={true}
								disabled={
									[PROMO_STATUSES.Completed, PROMO_STATUSES.Deactivated].includes(
										promo.status
									) || mode === DIALOG_EDIT_MODES.ViewOnly
								}
								onChange={(e) =>
									handlePromoFieldUpdate({ field: 'description', targetValue: e.target.value })
								}
							/>
						</div>
						<div className={classes.row}>
							<StyledTextField
								header="Promo Fine Print (External)"
								variant="filled"
								label="Note..."
								required={true}
								minRows={3}
								value={editedPromo.fine_print}
								tooltip={{
									tooltipChildren: (
										<div>
											Fine print must be added in order to save a new promo. Please refer to{' '}
											<a target="_blank" href={PROMO_GUIDELINES_LINK}>
												promo guidelines
											</a>{' '}
											if needed.
										</div>
									)
								}}
								multiline={true}
								// required={true}
								disabled={
									[PROMO_STATUSES.Completed, PROMO_STATUSES.Deactivated].includes(
										promo.status
									) || mode === DIALOG_EDIT_MODES.ViewOnly
								}
								onChange={(e) =>
									handlePromoFieldUpdate({ field: 'fine_print', targetValue: e.target.value })
								}
							/>
						</div>
						<div className={classes.row}>
							<div>
								<StyledSelect
									label="Discount Type"
									header="Discount Type"
									required={true}
									value={editedPromo.unit}
									disabled={
										[
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
									}
									onChange={(e) =>
										handlePromoFieldUpdate({ field: 'unit', targetValue: e.target.value })
									}>
									{Object.entries(FEE_VALUE_UNITS).map(([value, key]) => {
										return (
											<MenuItem key={key} value={value}>
												{key}
											</MenuItem>
										)
									})}
								</StyledSelect>
							</div>
							<div>
								{editedPromo.unit ===
									getKeyForValueInObject(FEE_VALUE_UNITS, FEE_VALUE_UNITS.cents) ? (
									<StyledCurrencyTextField
										id="promo-amount-cents"
										header="Promo Amount"
										variant="filled"
										label="Promo Amount"
										required={true}
										initialValue={editedPromo.value}
										disabled={
											[
												PROMO_STATUSES.Running,
												PROMO_STATUSES.Completed,
												PROMO_STATUSES.Deactivated
											].includes(promo.status) ||
											editedPromo.unit === '' ||
											mode === DIALOG_EDIT_MODES.ViewOnly
										}
										amountChanged={(e) => {
											handlePromoFieldUpdate({
												field: 'value',
												targetValue: e.inCents
											})
										}}
									/>
								) : (
									<StyledTextField
										id="promo-amount-percent"
										header="Promo Amount"
										variant="filled"
										label="Promo Amount"
										required={true}
										value={editedPromo.value}
										InputProps={{
											endAdornment:
												editedPromo.unit ===
													getKeyForValueInObject(
														FEE_VALUE_UNITS,
														FEE_VALUE_UNITS.percent
													) ? (
													<InputAdornment
														className={classes.amountAdornment}
														position="end">
														%
													</InputAdornment>
												) : null
										}}
										disabled={
											[
												PROMO_STATUSES.Running,
												PROMO_STATUSES.Completed,
												PROMO_STATUSES.Deactivated
											].includes(promo.status) ||
											editedPromo.unit === '' ||
											mode === DIALOG_EDIT_MODES.ViewOnly
										}
										onChange={(e) => {
											handlePromoFieldUpdate({
												field: 'value',
												targetValue: e.target.value
											})
										}}
									/>
								)}
							</div>
						</div>
						<div className={classes.row}>
							{editedPromo.unit ===
								getKeyForValueInObject(FEE_VALUE_UNITS, FEE_VALUE_UNITS.percent) ? (
								<div>
									<StyledCurrencyTextField
										header="Discount Limit"
										variant="filled"
										label="Discount Limit"
										initialValue={editedPromo.max_discount}
										required={true}
										disabled={
											[
												PROMO_STATUSES.Running,
												PROMO_STATUSES.Completed,
												PROMO_STATUSES.Deactivated
											].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
										}
										amountChanged={(e) =>
											handlePromoFieldUpdate({
												field: 'max_discount',
												targetValue: e.inCents
											})
										}
									/>
								</div>
							) : null}
							<StyledTextField
								header="Redemption Limit"
								variant="filled"
								label="Redemption Limit"
								value={editedPromo.redemptions_per_household}
								required={true}
								tooltip={{
									tooltipChildren: (
										<div>
											<p>This redemption limit will be applied per user</p>
											<p>
												For Play & Study promos, a redemption is required for each visit.
												Therefore, the redemption limit should be set to the number of
												sessions outlined in the Play & Study course at a minimum, or
												multiples of that number.
											</p>
										</div>
									),
									faIcon: 'fa-info-circle'
								}}
								disabled={
									[
										PROMO_STATUSES.Running,
										PROMO_STATUSES.Completed,
										PROMO_STATUSES.Deactivated
									].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
								}
								onChange={(e) =>
									handlePromoFieldUpdate({
										field: 'redemptions_per_household',
										targetValue: e.target.value
									})
								}
							/>
							{editedPromo.unit ===
								getKeyForValueInObject(FEE_VALUE_UNITS, FEE_VALUE_UNITS.percent) ? null : (
								<div>{NON_BREAKING_SPACE}</div>
							)}
						</div>
						<div className={classes.row}>
							<div>
								<StyledDatePicker
									disableDatePickerValidationErrors={true}
									open={isStartCalendarOpen}
									onOpen={() => setIsStartCalendarOpen(true)}
									onClose={() => setIsStartCalendarOpen(false)}
									InputProps={{
										startAdornment: <CalendarIconButton onClicked={setIsStartCalendarOpen} />
									}}
									required={true}
									disablePast={
										![
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status)
									}
									variant="inline"
									format="MM/DD/YYYY"
									margin="normal"
									id="start-date"
									header="Start Date"
									label={'Start Date'}
									inputVariant="filled"
									value={
										editedPromo.start_date
											? standardizeStartOfDateUTC(editedPromo.start_date)
											: null
									}
									className={classes.datePicker}
									disabled={
										[
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
									}
									onChange={(e) =>
										handlePromoFieldUpdate({
											field: 'start_date',
											targetValue: e.format('YYYY-MM-DD')
										})
									}
									rightArrowIcon={
										<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />
									}
									leftArrowIcon={
										<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />
									}
								/>
							</div>
							<div>
								<StyledDatePicker
									disableDatePickerValidationErrors={true}
									open={isEndCalendarOpen}
									onOpen={() => setIsEndCalendarOpen(true)}
									onClose={() => setIsEndCalendarOpen(false)}
									InputProps={{
										startAdornment: <CalendarIconButton onClicked={setIsEndCalendarOpen} />
									}}
									required={true}
									disablePast
									variant="inline"
									format="MM/DD/YYYY"
									margin="normal"
									id="start-date"
									header="End Date"
									label={'End Date'}
									inputVariant="filled"
									value={
										editedPromo.end_date ? standardizeEndOfDateUTC(editedPromo.end_date) : null
									}
									className={classes.datePicker}
									onChange={(e) =>
										handlePromoFieldUpdate({
											field: 'end_date',
											targetValue: e.format('YYYY-MM-DD')
										})
									}
									disabled={
										[PROMO_STATUSES.Completed, PROMO_STATUSES.Deactivated].includes(
											promo.status
										) || mode === DIALOG_EDIT_MODES.ViewOnly
									}
									rightArrowIcon={
										<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />
									}
									leftArrowIcon={
										<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />
									}
								/>
							</div>
						</div>
						{promoWithSameCodeAlreadyRunningOrScheduled ? (
							<div className={classes.row}>
								<StyledAlert
									type="error"
									text="A scheduled or running promo with the same code overlaps with those dates."
								/>
							</div>
						) : null}
						<div className={classes.row}>
							<div>
								<StyledMultiSelect
									header="Days of Week"
									label="Days of Week"
									disabled={
										[
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
									}
									values={editedPromo.applicable_days_of_week}
									onValuesChanged={(newDaysOfWeek) =>
										handlePromoFieldUpdate({
											field: 'applicable_days_of_week',
											targetValue: newDaysOfWeek
										})
									}
									tooltip={{
										tooltipChildren: (
											<div>
												<p>
													When days are added in this field, the promo will be limited to
													that particular day / days between the start date and end date.
												</p>
												<p>
													If no day of week is selected, the promo will apply to all
													dates within the start and end date.
												</p>
											</div>
										),
										faIcon: 'fa-info-circle'
									}}
									useChips={true}
									chipLabelRenderFunction={(value) => {
										return capitilizeMultipleWords(value)
									}}
									menuItems={Object.entries(PROMO_DAYS_OF_WEEK).map(([value, key]) => {
										return (
											<MenuItem key={key} value={value}>
												<Checkbox
													checked={
														editedPromo.applicable_days_of_week.indexOf(value) > -1
													}
												/>
												{key}
											</MenuItem>
										)
									})}
								/>
							</div>
							<div className={classes.timePickersContainer}>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '.5rem'
									}}>
									<Typography variant="overline">Time</Typography>
									{mode !== DIALOG_EDIT_MODES.ViewOnly ? (
										<StyledIconWithTooltip
											tooltipChildren={
												<div>
													<p>
														When times are added in this field, the promo will be
														limited to that particular time range between the start
														date and end date.
													</p>
													<p>
														If no time is selected, the promo will apply to all times
														within the start and end date.
													</p>
													<p>
														This time is in a 24-hour clock format and will be applied
														to the timezone for the selected market.
													</p>
												</div>
											}
										/>
									) : null}
								</div>
								<div className={classes.timePickersContent}>
									<div>
										<StyledKeyboardTimePicker
											disableFuture
											ampm={false}
											format="HH:mm"
											label="Start Time"
											initialvalue={editedPromo.start_time}
											disabled={!enableTimeEditing || mode === DIALOG_EDIT_MODES.ViewOnly}
											timeChanged={(e) => {
												const { formatted } = e
												handlePromoFieldUpdate({
													field: 'start_time',
													targetValue: formatted
												})
											}}
										/>
									</div>
									<div>
										<StyledKeyboardTimePicker
											disableFuture
											format="HH:mm"
											ampm={false}
											label="End Time"
											initialvalue={editedPromo.end_time}
											disabled={!enableTimeEditing || mode === DIALOG_EDIT_MODES.ViewOnly}
											timeChanged={(e) => {
												const { formatted } = e
												handlePromoFieldUpdate({
													field: 'end_time',
													targetValue: formatted
												})
											}}
										/>
									</div>
								</div>
								{editedPromo.start_time &&
									editedPromo.end_time &&
									endTimeToMoment.isBefore(startTimeToMoment) ? (
									<div className={classes.row}>
										<StyledAlert type="error" text="End time cannot be before start time." />
									</div>
								) : null}
							</div>
						</div>
						<div className={classes.row}>
							<div>
								<StyledMultiSelect
									header="Facilities"
									label="Facilities"
									required={true}
									disabled={
										lockToFacilityId ||
										!allowMultipleFacilities ||
										[
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) ||
										mode === DIALOG_EDIT_MODES.ViewOnly
									}
									values={editedPromo.applicable_facilities || []}
									onValuesChanged={(newFacilities) => {
										handleFacilityFieldUpdate(newFacilities)
									}}
									useChips={true}
									chipLabelRenderFunction={(facilityId) => {
										const facility = (facilities || []).find(
											(item) => String(item._id) === String(facilityId)
										)
										return facility?.name || facilityId
									}}
									menuItems={(facilities || [])
										.filter(
											(facility) =>
												facility.active ||
												(editedPromo.applicable_facilities || []).some(
													(id) => String(id) === String(facility._id)
												)
										)
										.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
										.map((facility) => {
											return (
												<MenuItem key={facility._id} value={facility._id}>
													<Checkbox
														checked={(editedPromo.applicable_facilities || []).some(
															(id) => String(id) === String(facility._id)
														)}
													/>
													{facility.name}
												</MenuItem>
											)
										})}
								/>
							</div>
							<div>
								<StyledMultiSelect
									header="Service Targets"
									label="Service Targets"
									required={true}
									disabled={
										[
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
									}
									values={editedPromo.applicable_services}
									onValuesChanged={(newServices) =>
										handlePromoFieldUpdate({
											field: 'applicable_services',
											targetValue: newServices
										})
									}
									useChips={true}
									chipLabelRenderFunction={(value) => {
										return PROMO_SERVICE_TARGET_OPTIONS[value]
									}}
									menuItems={Object.entries(PROMO_SERVICE_TARGET_OPTIONS)
										.filter(([key, _]) => {
											// not allowing these at creation just yet. only for VIPMEMBER currently
											return key !== PROMO_SERVICE_TARGETS.ADDON_ONLY_VISIT
										})
										.map(([key, value]) => {
											return (
												<MenuItem key={key} value={key}>
													<Checkbox
														checked={editedPromo.applicable_services.indexOf(key) > -1}
													/>
													{value}
												</MenuItem>
											)
										})}
								/>
							</div>
						</div>

						{!editedPromo.applicable_services?.includes('memberships') && (
							<div className={classes.row}>
								<StyledMultiSelect
									header="Membership Targets"
									label="Membership Targets"
									required={false}
									disabled={
										[
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
									}
									values={editedPromo.applicable_memberships}
									onValuesChanged={(newMems) =>
										handlePromoFieldUpdate({
											field: 'applicable_memberships',
											targetValue: newMems
										})
									}
									useChips={true}
									chipLabelRenderFunction={(value) => {
										return PROMO_MEMBERSHIP_TARGET_OPTIONS[value]
									}}
									menuItems={Object.entries(PROMO_MEMBERSHIP_TARGET_OPTIONS).map(
										([value, key]) => {
											return (
												<MenuItem key={key} value={value}>
													<Checkbox
														checked={
															editedPromo.applicable_memberships.indexOf(value) > -1
														}
													/>
													{key}
												</MenuItem>
											)
										}
									)}
								/>
								{editedPromo.applicable_services.length &&
									editedPromo.applicable_services.every((service) =>
										[
											PROMO_SERVICE_TARGETS.BOARDING,
											PROMO_SERVICE_TARGETS.BOARDING_WITH_CREDITS
										].includes(service)
									) ? (
									<StyledTextField
										header="Minimum Number of Nights Required"
										variant="filled"
										label="Minimum Number of Nights Required"
										value={editedPromo.min_number_of_nights}
										disabled={
											[
												PROMO_STATUSES.Running,
												PROMO_STATUSES.Completed,
												PROMO_STATUSES.Deactivated
											].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
										}
										onChange={(e) =>
											handlePromoFieldUpdate({
												field: 'min_number_of_nights',
												targetValue: e.target.value
											})
										}
									/>
								) : (
									<div>{NON_BREAKING_SPACE}</div>
								)}
							</div>
						)}
						{editedPromo.applicable_services?.includes('memberships') && (
							<div className={classes.row}>
								<StyledMultiSelect
									header="Membership Type Targets"
									label="Membership Type Targets"
									required={true}
									disabled={
										[
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
									}
									values={editedPromo.membership_type_targets}
									onValuesChanged={(newMems) =>
										handlePromoFieldUpdate({
											field: 'membership_type_targets',
											targetValue: newMems
										})
									}
									useChips={true}
									chipLabelRenderFunction={(value) => {
										return PROMO_MEMBERSHIP_TYPE_TARGET_OPTIONS[value]
									}}
									menuItems={Object.entries(PROMO_MEMBERSHIP_TYPE_TARGET_OPTIONS).map(
										([value, key]) => {
											return (
												<MenuItem key={key} value={value}>
													<Checkbox
														checked={
															editedPromo.membership_type_targets.indexOf(value) > -1
														}
													/>
													{key}
												</MenuItem>
											)
										}
									)}
								/>
								<StyledSelect
									label="Renewal Limit"
									header="Renewal Limit"
									required={true}
									value={editedPromo.renewal_limit}
									tooltip={{
										placement: 'top-end',
										tooltipChildren: (
											<div>
												This field indicates the number of times the user will be able to
												renew their membership with the indicated discount. For a monthly
												membership, the renewal limit is in months and for an annual
												membership the renewal limit is in years. For example, "0" means no
												additional discounts will be applied moving forward when the
												membership renews. "1" means the user will get one additional
												renewal with the discount applied.
											</div>
										)
									}}
									disabled={
										[
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
									}
									onChange={(e) =>
										handlePromoFieldUpdate({
											field: 'renewal_limit',
											targetValue: e.target.value
										})
									}>
									{Object.entries(RENEWAL_LIMITS).map(([value, key]) => {
										return (
											<MenuItem key={key} value={value}>
												{key}
											</MenuItem>
										)
									})}
								</StyledSelect>
							</div>
						)}

						{editedPromo.applicable_services?.includes(TRAINING) ? (
							<div className={classes.row}>
								<StyledMultiSelect
									header="Applicable Training Courses"
									label="Applicable Training Courses"
									required={true}
									disabled={
										[
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
									}
									values={editedPromo.applicable_training_courses}
									onValuesChanged={(courses) =>
										handlePromoFieldUpdate({
											field: 'applicable_training_courses',
											targetValue: courses
										})
									}
									useChips={true}
									chipLabelRenderFunction={(value) => {
										const course = filteredTrainingCourseIdentifiers.find(
											({ abbreviation }) => abbreviation === value
										)
										return course?.name || value
									}}
									menuItems={filteredTrainingCourseIdentifiers?.map(
										({ _id, name, abbreviation }) => {
											return (
												<MenuItem key={_id} value={abbreviation}>
													<Checkbox
														checked={
															editedPromo.applicable_training_courses.indexOf(
																abbreviation
															) > -1
														}
													/>
													{name}
												</MenuItem>
											)
										}
									)}
								/>
								<div>{NON_BREAKING_SPACE}</div>
							</div>
						) : null}

						<div className={classes.row}>
							<div className={classes.switchContainer}>
								<StyledSwitchWithLabel
									disabled={
										[
											PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
									}
									label="New Customers Only"
									tooltip={{
										tooltipText:
											'The new customers toggle applies to new customers that have not completed an appointment for the selected service targets.'
									}}
									checked={editedPromo.new_customers_only}
									onChecked={(e) => {
										handlePromoFieldUpdate({
											field: 'new_customers_only',
											targetValue: e.target.checked
										})
									}}
								/>
							</div>
							<div className={classes.switchContainer}>
								<StyledSwitchWithLabel
									disabled={
										[
											// PROMO_STATUSES.Running,
											PROMO_STATUSES.Completed,
											PROMO_STATUSES.Deactivated
										].includes(promo.status) ||
										mode === DIALOG_EDIT_MODES.ViewOnly ||
										wouldExceedClientAppLimit
									}
									label="Show in Client App"
									tooltip={{
										tooltipText: wouldExceedClientAppLimit
											? `Maximum of ${CLIENT_APP_PROMO_LIMIT} active promos can be displayed in the client app. Currently ${activeClientAppPromosCount} active promos are displayed.`
											: `When enabled, this promotion will be visible to clients in the client app. Maximum of ${CLIENT_APP_PROMO_LIMIT} active promos can be displayed at once.`
									}}
									checked={editedPromo.show_in_client_app}
									onChecked={(e) => {
										if (wouldExceedClientAppLimit) {
											return
										}
										handlePromoFieldUpdate({
											field: 'show_in_client_app',
											targetValue: e.target.checked
										})
									}}
								/>
							</div>
						</div>

						{editedPromo.show_in_client_app ? (
							<>
								<div className={classes.row}>
									<div style={{ width: '100%' }}>
										<TextField
											label="Upload Photo"
											variant="filled"
											style={{
												width: '100%',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												overflow: 'hidden'
											}}
											noWrap
											onChange={() => { }}
											value={editedPromo.image_url || editedPromo?.temp_image_file?.name || ''}
											required={editedPromo.show_in_client_app ? true : false}
											disabled={
												[
													PROMO_STATUSES.Completed,
													PROMO_STATUSES.Deactivated
												].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
											}
											InputProps={{
												style: {
													width: '100%',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
													overflow: 'hidden'
												},
												endAdornment: (
													<InputAdornment position="end">
														{imageLoading ? (
															<CircularProgress size={40} color="primary" />
														) : (
															<label htmlFor="promo-image" style={{}}>
																<Button
																	component="span"
																	style={{
																		fontSize: 14,
																		fontWeight: '700',
																		color: '#6B4AD6',
																		textTransform: 'capitalize'
																	}}
																	disabled={
																		[
																			PROMO_STATUSES.Completed,
																			PROMO_STATUSES.Deactivated
																		].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
																	}>
																	Upload
																</Button>
															</label>
														)}
													</InputAdornment>
												)
											}}
										/>
										<div className={classes.fileUpload}>
											<input
												hidden
												accept="image/*"
												className={classes.input}
												id="promo-image"
												type="file"
												ref={promoImageInputRef}
												onChange={(e) => uploadPromoImage(e)}
											/>
										</div>
									</div>
								</div>
								<div className={classes.row}>
									<StyledTextField
										header="Headline"
										variant="filled"
										label="Headline"
										value={editedPromo.headline || ''}
										required={true}
										disabled={
											[
												PROMO_STATUSES.Completed,
												PROMO_STATUSES.Deactivated
											].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
										}
										onChange={(e) =>
											handlePromoFieldUpdate({
												field: 'headline',
												targetValue: e.target.value
											})
										}
									/>
								</div>
								<div className={classes.row}>
									<div>
										<StyledSelect
											label="Link Type"
											header="Link Type"
											required={editedPromo.show_in_client_app ? true : false}
											value={editedPromo.link_type || ''}
											disabled={
												[
													PROMO_STATUSES.Completed,
													PROMO_STATUSES.Deactivated
												].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
											}
											onChange={(e) =>
												handlePromoFieldUpdate({
													field: 'link_type',
													targetValue: e.target.value
												})
											}>
											{Object.entries(PROMO_LINK_TYPES).map(([key, value]) => {
												return (
													<MenuItem key={value} value={value}>
														{key}
													</MenuItem>
												)
											})}
										</StyledSelect>
									</div>
									{editedPromo.link_type === PROMO_LINK_TYPES.Web ? (
										<div>
											<StyledTextField
												header="Web Link URL"
												variant="filled"
												label="Web Link URL"
												value={editedPromo.web_link_url || ''}
												required={true}
												disabled={
													[
														PROMO_STATUSES.Completed,
														PROMO_STATUSES.Deactivated
													].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
												}
												onChange={(e) =>
													handlePromoFieldUpdate({
														field: 'web_link_url',
														targetValue: e.target.value
													})
												}
											/>
										</div>
									) : editedPromo.link_type === PROMO_LINK_TYPES.App ? (
										<div>
											<StyledSelect
												label="App Link Target"
												header="App Link Target"
												required={true}
												value={editedPromo.app_link_target || ''}
												disabled={
													[
														PROMO_STATUSES.Completed,
														PROMO_STATUSES.Deactivated
													].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
												}
												onChange={(e) =>
													handlePromoFieldUpdate({
														field: 'app_link_target',
														targetValue: e.target.value
													})
												}>
												{Object.entries(PROMO_APP_LINK_TARGETS).map(([key, value]) => {
													return (
														<MenuItem key={value} value={value}>
															{key}
														</MenuItem>
													)
												})}
											</StyledSelect>
										</div>
									) : (
										<div>{NON_BREAKING_SPACE}</div>
									)}
								</div>
								<div className={classes.row}>
									<StyledTextField
										header="Short Description"
										variant="filled"
										label="Short Description"
										value={editedPromo.short_description || ''}
										required={editedPromo.show_in_client_app ? true : false}
										multiline={true}
										minRows={2}
										disabled={
											[
												PROMO_STATUSES.Completed,
												PROMO_STATUSES.Deactivated
											].includes(promo.status) || mode === DIALOG_EDIT_MODES.ViewOnly
										}
										onChange={(e) =>
											handlePromoFieldUpdate({
												field: 'short_description',
												targetValue: e.target.value
											})
										}
									/>
								</div>
							</>
						) : null}
						<div className={classes.dialogActions}>
							{mode !== DIALOG_EDIT_MODES.ViewOnly ? (
								<MainButton
									label={mode === DIALOG_EDIT_MODES.New ? 'SAVE' : 'UPDATE'}
									onClick={handleOnSave}
									variant="contained"
									disabled={!canSave}
								/>
							) : null}

							<MainButton label="Cancel" onClick={handleOnCancelled} />
							{editedPromo.applicable_services.includes(BOARDING) &&
								editedPromo.applicable_days_of_week.length > 0 ? (
								<StyledAlert
									type="error"
									text="Promos that include boarding must not have applicable days of the week."
								/>
							) : null}
							{startDateToMoment.isAfter(endDateToMoment) ? (
								<StyledAlert type="error" text="Start date is after end date." />
							) : null}
							{editedPromo.code && codeContainsSpecialCharacters ? (
								<StyledAlert
									type="error"
									text="Promo code must be 5 to 15 characters long and contain only letters and numbers."
								/>
							) : null}
							{promoIncludesUnitNotAllowedForServiceTarget.map((error) => {
								return <StyledAlert type="error" text={error} />
							})}
							{mode !== DIALOG_EDIT_MODES.ViewOnly
								? saveBlockers.map((blocker) => (
										<StyledAlert key={blocker} type="error" text={blocker} />
								  ))
								: null}
						</div>
					</div>
				</DialogContent>
			</StyledDialog>
			<DangerConfirmationDialog
				open={confirmEndPromoDialogOpen}
				description="Are you sure you want to end this promotion? Ending it will result in immediate deactivation, rendering the promo code unusable at checkout. If a client has already applied the code to a booking, it will still be honored on their invoice."
				dangerButtonText="End Promo"
				onConfirmDangerousAction={handleEndPromo}
				onCancel={handleCloseConfirmEndPromoDialog}
			/>
		</>
	)
}

export default PromoDialog
